// ./src/pages/private/EventTicketsPage/TicketRulesDrawer.tsx
//
// Manage the variable-pricing rules for one ticket type. Opened from the ticket
// card. Rules apply in evaluation order (order decides which wins). In normal mode
// each rule is a swipe-to-reveal card (phones) / card with buttons (wider); a
// Reorder toggle switches to drag-handle cards. "Add rule" opens the
// PriceRuleDrawer stacked on top. Changes save immediately.
// #162 — "Price by tickets sold" sits above the rules: the ladder ("the first 40 at $10, the next 40 at $15") is edited as
// tiers in PriceTiersDrawer and saved in one call; its rules never appear in the list below (they are not edited one by
// one, and a tier dragged among the other rules would change what it means).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Stack, Group, Text, Button, Card, Badge, Box, ActionIcon, Tooltip } from '@mantine/core';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useTranslation } from 'react-i18next';
import { formatCurrency, formatDateTime } from '@em/utils';

import {
  TicketType,
  TicketPriceRule,
  TicketPriceRuleService,
  TICKET_PRICE_RULE_KIND,
  TICKET_PRICE_EFFECT_KIND,
} from '@/utils/api';
import { useTicketPriceRules, useToast } from '@/hooks';
import { AppDrawer, AppIcon, EmptyState, ErrorAlert, ConfirmModal, SwipeableCard, SwipeAction, useSwipeableList, PremiumFeatureGate, FeatureGateContent, CardListSkeleton } from '@/components/generic';
import { FEATURE_CODES } from '@/config';
import { PriceRuleDrawer } from './PriceRuleDrawer';
import { PriceTiersDrawer } from './PriceTiersDrawer';
import { isTierRule, tiersOf } from './priceTiers';
import { runBulkWrites, refusalReason } from '@/utils';

const DAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface TicketRulesDrawerProps {
  opened: boolean;
  onClose: () => void;
  ticket: TicketType | null;
  eventCurrency?: string | null;
  /** The event's time zone — an early-bird window is shown, and entered, on the event's clock (ROADMAP #93). ⚠ Required. */
  timezone: string | null;
  /** Reload the tickets list (to refresh the rule-count badge) after changes. */
  onChanged: () => void;
  /**
   * The account's plan does not include `promotions`.
   *
   * ⚠ THE DRAWER STILL OPENS — it shows what pricing rules ARE and offers Subscribe, instead of
   * the row action bouncing the host to the billing page (owner, 2026-09-12). Computed ONCE by
   * the page from the single resolver and passed down; this component must not ask again.
   *
   * ⚠ REQUIRED, AND IT HAS NO DEFAULT ON PURPOSE (`no-defaults-on-gating-flags`). `locked = false`
   * means a call site that forgets the prop silently gets the fully unlocked PROMOTIONS editor and
   * `tsc` says nothing — the gate would be absent, not merely wrong. Required moves that to compile
   * time: a new caller cannot mount this drawer without stating the plan's answer.
   */
  locked: boolean;
}

export const TicketRulesDrawer: React.FC<TicketRulesDrawerProps> = ({ opened, onClose, ticket, eventCurrency, timezone, onChanged, locked }) => {
  const { t } = useTranslation(['events', 'common']);
  const toast = useToast();
  const ticketTypeId = ticket?.id ?? null;
  const currency = ticket?.currencyCode || eventCurrency || 'USD';
  // ⚠ formatCurrency says WHICH currency ("US$10.00") — never a symbol glued on by hand.
  const money = (n: number): string => formatCurrency(n, currency);

  // ⚠ A locked drawer loads NOTHING. There is no list to show, and the server would refuse a
  // write anyway — fetching would be a request made purely to throw the answer away.
  const { rules, isLoaded, error: rulesError, reload } = useTicketPriceRules(opened && !locked ? ticketTypeId : null);

  const [formOpen, setFormOpen] = useState(false);
  const [tiersOpen, setTiersOpen] = useState(false);
  const [editing, setEditing] = useState<TicketPriceRule | null>(null);
  const [deleteRule, setDeleteRule] = useState<TicketPriceRule | null>(null);
  const [orderOverride, setOrderOverride] = useState<TicketPriceRule[] | null>(null);
  const [reorderMode, setReorderMode] = useState(false);
  const [busy, setBusy] = useState(false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();

  const toggleReorder = () =>
    setReorderMode((m) => {
      if (m) setOrderOverride(null);
      return !m;
    });

  // Reset reorder state each time the drawer closes (fresh for the next ticket).
  useEffect(() => {
    if (!opened) {
      setReorderMode(false);
      setOrderOverride(null);
    }
  }, [opened]);

  // The ladder's rules are the tiers section; every other rule is the list (and its order).
  const tiers = useMemo(() => tiersOf(rules), [rules]);
  const sorted = useMemo(
    () =>
      rules
        .filter((r) => !isTierRule(r))
        .sort((a, b) => (a.orderNo ?? 0) - (b.orderNo ?? 0) || (a.createdAt ?? '').localeCompare(b.createdAt ?? '')),
    [rules],
  );
  const list = orderOverride ?? sorted;

  const afterChange = useCallback(async () => {
    setOrderOverride(null);
    await reload();
    onChanged();
  }, [reload, onChanged]);

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (r: TicketPriceRule) => {
    setEditing(r);
    setFormOpen(true);
  };

  const nextOrderNo = useMemo(() => {
    const max = sorted.reduce((m, r) => Math.max(m, Number(r.orderNo ?? 0)), 0);
    return max + 1000;
  }, [sorted]);

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { source, destination } = result;

      if (!destination || destination.index === source.index) {
        return;
      }

      const base = orderOverride ?? sorted;
      const next = [...base];
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      setOrderOverride(next);

      if (!moved.id) {
        return;
      }

      const to = destination.index;
      const prevNo = to > 0 ? Number(next[to - 1].orderNo ?? 0) : 0;
      const nextItem = next[to + 1];
      const nextNo = nextItem ? Number(nextItem.orderNo ?? 0) : prevNo + 2000;
      const updates =
        nextNo - prevNo > 0.001
          ? [{ id: moved.id, orderNo: prevNo + (nextNo - prevNo) / 2 }]
          : next.filter((r) => r.id).map((r, i) => ({ id: r.id as string, orderNo: (i + 1) * 1000 }));

      setBusy(true);
      const results = await runBulkWrites(updates, (u) => TicketPriceRuleService.update(u.id, { orderNo: u.orderNo }));
      setBusy(false);

      // ⚠ These results used to be discarded. `reload()` below restores the SERVER order,
      // so a refused write made the dragged rule silently snap back to where it started.
      const failed = results.find((r) => !r.didSucceed());

      if (failed) {
        toast.error(refusalReason(failed) || t('events:rules.reorderFailed', { defaultValue: 'Could not save the new order.' }));
      }

      await reload();
      onChanged();
    },
    [orderOverride, sorted, reload, onChanged, toast, t],
  );

  const confirmDelete = useCallback(async () => {
    const target = deleteRule;
    setDeleteRule(null);

    if (!target?.id) {
      return;
    }

    const result = await TicketPriceRuleService.remove(target.id);

    // No success toast — the row disappearing IS the confirmation (house rule).
    if (result.didSucceed()) {
      afterChange();
    } else {
      toast.error(refusalReason(result) || t('events:rules.removeFailed', { defaultValue: 'Could not remove the rule.' }));
    }
  }, [deleteRule, afterChange, toast, t]);

  // "20% off" / "+$5" / "Set to $50".
  const effectSummary = (r: TicketPriceRule): string => {
    const amt = r.amount ?? 0;
    if (r.effectKind === TICKET_PRICE_EFFECT_KIND.SET_PRICE) {
      return t('events:rules.sumSet', { defaultValue: 'Set to {{price}}', price: money(amt) });
    }
    if (r.effectKind === TICKET_PRICE_EFFECT_KIND.ADJUST_PERCENT) {
      return amt < 0
        ? t('events:rules.sumPctOff', { defaultValue: '{{n}}% off', n: Math.abs(amt) })
        : t('events:rules.sumPctUp', { defaultValue: '+{{n}}%', n: amt });
    }
    return amt < 0
      ? t('events:rules.sumAmtOff', { defaultValue: '{{v}} off', v: money(Math.abs(amt)) })
      : t('events:rules.sumAmtUp', { defaultValue: '+{{v}}', v: money(amt) });
  };

  // "until Jun 30" / "Fri–Sun 18:00–23:00" / "Members".
  const conditionSummary = (r: TicketPriceRule): string => {
    if (r.ruleKind === TICKET_PRICE_RULE_KIND.DATE_WINDOW) {
      const s = r.startsAt ? formatDateTime(r.startsAt, 'MMM D', timezone || undefined) : null;
      const e = r.endsAt ? formatDateTime(r.endsAt, 'MMM D', timezone || undefined) : null;
      if (s && e) return `${s} – ${e}`;
      if (e) return t('events:rules.until', { defaultValue: 'until {{d}}', d: e });
      if (s) return t('events:rules.from', { defaultValue: 'from {{d}}', d: s });
      return t('events:rules.kindDate', { defaultValue: 'Early bird (date range)' });
    }
    if (r.ruleKind === TICKET_PRICE_RULE_KIND.TIME_OF_WEEK) {
      const days = (r.daysOfWeek ?? []).map((d) => t(`events:rules.day.${DAY_SHORT[d]}`, { defaultValue: DAY_SHORT[d] })).join(', ');
      const time = r.timeFrom && r.timeTo ? `${r.timeFrom}–${r.timeTo}` : '';
      return [days, time].filter(Boolean).join(' · ') || t('events:rules.kindTime', { defaultValue: 'Time of week' });
    }
    if (r.ruleKind === TICKET_PRICE_RULE_KIND.VOLUME) {
      return t('events:rules.volumeFrom', { defaultValue: '{{n}} or more in one order — each ticket', n: r.minQuantity ?? 1 });
    }
    if (r.ruleKind === TICKET_PRICE_RULE_KIND.MEMBERSHIP) {
      return r.membershipTypeId
        ? t('events:rules.membersType', { defaultValue: 'Specific membership' })
        : t('events:rules.anyMember', { defaultValue: 'Any member' });
    }
    return '';
  };

  // Name + effect + stack badge + condition subline. `dragHandle` in reorder mode.
  const ruleRow = (r: TicketPriceRule, dragHandle: React.ReactNode) => (
    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
      {dragHandle}
      <Box style={{ minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text fw={600} size="md" truncate>
            {r.name || conditionSummary(r)}
          </Text>
          <Badge variant="light" color="indigo" size="sm">
            {effectSummary(r)}
          </Badge>
          {r.isStackable && (
            <Badge variant="light" color="teal" size="sm">
              {t('events:rules.stackBadge', { defaultValue: 'Stacks' })}
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed" truncate>
          {conditionSummary(r)}
        </Text>
      </Box>
    </Group>
  );

  const rowActions = (r: TicketPriceRule) => (
    <Group gap={4} wrap="nowrap">
      <Tooltip label={t('common:buttons.edit', { defaultValue: 'Edit' })}>
        <ActionIcon variant="subtle" color="gray" onClick={() => openEdit(r)}>
          <AppIcon iconName="Edit" size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t('common:buttons.delete', { defaultValue: 'Delete' })}>
        <ActionIcon variant="subtle" color="red" onClick={() => setDeleteRule(r)}>
          <AppIcon iconName="Trash" size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );

  const swipeActionsFor = (r: TicketPriceRule): SwipeAction[] => [
    { key: 'edit', icon: 'Edit', label: t('common:buttons.edit', { defaultValue: 'Edit' }), color: 'blue', onClick: () => { closeAllCards(); openEdit(r); } },
    { key: 'delete', icon: 'Trash', label: t('common:buttons.delete', { defaultValue: 'Delete' }), color: 'red', onClick: () => { closeAllCards(); setDeleteRule(r); } },
  ];

  const cardBody = (r: TicketPriceRule, showActions: boolean) => (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" gap="sm">
        {ruleRow(r, null)}
        {showActions && rowActions(r)}
      </Group>
    </Card>
  );

  // Reorder mode — drag-handle cards (both breakpoints), no swipe/actions.
  const renderReorder = () => (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="rules">
        {(provided) => (
          <Stack gap="xs" ref={provided.innerRef} {...provided.droppableProps}>
            {list
              .filter((r) => r.id)
              .map((r, index) => (
                <Draggable key={r.id} draggableId={r.id as string} index={index}>
                  {(prov, snapshot) => {
                    const card = (
                      <Card
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        withBorder
                        radius="md"
                        padding="sm"
                        style={{ ...prov.draggableProps.style, boxShadow: snapshot.isDragging ? 'var(--mantine-shadow-md)' : undefined }}
                      >
                        {ruleRow(
                          r,
                          <Box {...prov.dragHandleProps} c="dimmed" style={{ cursor: 'grab', display: 'flex' }}>
                            <AppIcon iconName="GripHorizontal" size={18} />
                          </Box>,
                        )}
                      </Card>
                    );
                    // Portal the dragging clone to <body> so the drawer's CSS
                    // transform doesn't clip its position:fixed placement.
                    return snapshot.isDragging ? createPortal(card, document.body) : card;
                  }}
                </Draggable>
              ))}
            {provided.placeholder}
          </Stack>
        )}
      </Droppable>
    </DragDropContext>
  );

  // Normal mode — phones swipe to reveal actions; wider shows action buttons.
  const renderNormal = () => (
    <Stack gap="xs">
      {sorted
        .filter((r) => r.id)
        .map((r) => (
          <React.Fragment key={r.id}>
            <Box hiddenFrom="sm">
              <SwipeableCard
                leftSwipeActions={swipeActionsFor(r)}
                forceClose={shouldForceClose(r.id || '')}
                onForceCloseComplete={() => handleForceCloseComplete(r.id || '')}
                onSwipeStateChange={(isOpen) => isOpen && openCard(r.id || '')}
                borderRadius="var(--mantine-radius-md)"
              >
                {cardBody(r, false)}
              </SwipeableCard>
            </Box>
            <Box visibleFrom="sm">{cardBody(r, true)}</Box>
          </React.Fragment>
        ))}
    </Stack>
  );

  // #162 — the tier ladder: its rows, or an offer to add one.
  const tiersSection = () => (
    <Card withBorder radius="md" padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap="sm">
        <Box style={{ minWidth: 0 }}>
          <Text fw={600} size="md">
            {t('events:tiers.title', { defaultValue: 'Price by tickets sold' })}
          </Text>
          {tiers.length === 0 ? (
            <Text size="md" c="dimmed">
              {t('events:tiers.offer', { defaultValue: 'E.g. the first 40 at one price, the next 40 at another, then the regular price.' })}
            </Text>
          ) : (
            <Stack gap={2} mt={4}>
              {tiers.map((tier) => (
                <Text key={tier.from} size="md">
                  {tier.from === tier.to
                    ? t('events:tiers.rowOne', { defaultValue: 'Ticket {{from}}: {{price}}', from: tier.from, price: money(tier.price) })
                    : t('events:tiers.row', { defaultValue: 'Tickets {{from}}–{{to}}: {{price}}', from: tier.from, to: tier.to, price: money(tier.price) })}
                </Text>
              ))}
              <Text size="md" c="dimmed">
                {t('events:tiers.then', { defaultValue: 'Then: the regular price ({{price}})', price: money(Number(ticket?.price ?? 0)) })}
              </Text>
            </Stack>
          )}
        </Box>
        <Button variant="light" size="md" onClick={() => setTiersOpen(true)} style={{ flexShrink: 0 }}>
          {tiers.length === 0 ? t('events:tiers.addButton', { defaultValue: 'Set tiers' }) : t('common:buttons.edit', { defaultValue: 'Edit' })}
        </Button>
      </Group>
    </Card>
  );

  const body = () => {
    if (locked) {
      return (
        <PremiumFeatureGate
          featureCode={FEATURE_CODES.PROMOTIONS}
          eventId={ticket?.eventId ?? null}
          variant="panel"
          featureDescription={<FeatureGateContent gateKey="ticketPriceRules" featureCode={FEATURE_CODES.PROMOTIONS} />}
        >
          {null}
        </PremiumFeatureGate>
      );
    }

    if (!isLoaded) {
      return (
        <CardListSkeleton rows={3} actions={2} lines={2} lineSizes={['md', 'sm']} lineGap={0} />
      );
    }
    // A failed read is never drawn as an empty list (em-scaffold-ui: not read yet → error → first run).
    if (rulesError) {
      return (
        <Stack gap="sm">
          <ErrorAlert
            error={rulesError}
            title={t('events:ticketRules.listLoadError', { defaultValue: 'Couldn’t load the price rules' })}
            page="Ticket price rules"
            operation="ticketPriceRuleList"
          />
          {/* A drawer has no header Refresh — the way back from a failed read is here. */}
          <Button variant="light" leftSection={<AppIcon iconName="Refresh" size={16} />} onClick={() => void reload()} style={{ alignSelf: 'flex-start' }}>
            {t('common:tryAgain', { defaultValue: 'Try again' })}
          </Button>
        </Stack>
      );
    }

    if (sorted.length === 0) {
      return (
        <Stack gap="sm">
        {tiersSection()}
        <EmptyState
          iconName="Discount"
          title={t('events:rules.emptyTitle', { defaultValue: 'No pricing rules' })}
          description={t('events:rules.emptyDesc', {
            defaultValue: 'Add early-bird, time-of-week, membership or "buy more, pay less" pricing. The base ticket price applies when no rule matches.',
          })}
          actionLabel={t('events:rules.add', { defaultValue: 'Add rule' })}
          actionIcon="Plus"
          onAction={openAdd}
        />
        </Stack>
      );
    }

    return (
      <Stack gap="sm">
        {tiersSection()}
        <Group justify="space-between" wrap="nowrap" align="flex-start">
          <Text size="sm" c="dimmed">
            {reorderMode
              ? t('events:rules.orderHint', { defaultValue: 'Rules are checked top to bottom — drag to reorder. A non-stacking rule stops the search once it matches.' })
              : t('events:rules.orderNote', { defaultValue: 'Rules are checked top to bottom; the base price applies when none match.' })}
          </Text>
          {sorted.length > 1 && (
            <Button
              size="md"
              variant={reorderMode ? 'filled' : 'light'}
              color={reorderMode ? undefined : 'gray'}
              leftSection={<AppIcon iconName={reorderMode ? 'Check' : 'GripHorizontal'} size={14} />}
              onClick={toggleReorder}
              loading={busy}
              style={{ flexShrink: 0 }}
            >
              {reorderMode ? t('events:rules.doneReorder', { defaultValue: 'Done' }) : t('events:rules.reorder', { defaultValue: 'Reorder' })}
            </Button>
          )}
        </Group>

        {reorderMode ? renderReorder() : renderNormal()}

        {!reorderMode && (
          <Button variant="light" leftSection={<AppIcon iconName="Plus" size={16} />} onClick={openAdd} loading={busy} mt="xs">
            {t('events:rules.add', { defaultValue: 'Add rule' })}
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <>
      <AppDrawer
        opened={opened}
        onClose={onClose}
        title={
          ticket
            ? t('events:rules.drawerTitle', { defaultValue: 'Pricing rules — {{name}}', name: ticket.name ?? '' })
            : t('events:rules.drawerTitleBare', { defaultValue: 'Pricing rules' })
        }
        size="md"
        actionType="close"
        cancelLabel={t('common:buttons.close', { defaultValue: 'Close' })}
      >
        {body()}
      </AppDrawer>

      {ticketTypeId && (
        <PriceTiersDrawer
          opened={tiersOpen}
          onClose={() => setTiersOpen(false)}
          ticketTypeId={ticketTypeId}
          regularPrice={Number(ticket?.price ?? 0)}
          currencyCode={currency}
          initial={tiers}
          onSaved={afterChange}
        />
      )}

      {ticketTypeId && (
        <PriceRuleDrawer
          opened={formOpen}
          onClose={() => setFormOpen(false)}
          ticketTypeId={ticketTypeId}
          currencyCode={currency}
          timezone={timezone}
          rule={editing}
          defaultOrderNo={nextOrderNo}
          onSaved={afterChange}
        />
      )}

      <ConfirmModal
        opened={!!deleteRule}
        onClose={() => setDeleteRule(null)}
        onConfirm={confirmDelete}
        type="danger"
        title={t('events:rules.removeTitle', { defaultValue: 'Remove this rule?' })}
        message={t('events:rules.removeMessage', { defaultValue: 'The base ticket price will apply when this rule would have.' })}
        confirmLabel={t('common:buttons.remove', { defaultValue: 'Remove' })}
        cancelLabel={t('common:buttons.cancel', { defaultValue: 'Cancel' })}
      />
    </>
  );
};
